import React, { JSX, useEffect, useState } from "react";
import { Table, Spinner, Card, Badge } from "react-bootstrap";
import { horizon } from "../api/horizon";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Link } from "react-router-dom";
import CopyIcon from "../components/CopyIcon";
import { okx } from "../api/okx";
import { CardContent } from "../components/Card";
import { coreTeamAddress, LabledAddress } from "../utils/addresss";
import AccountLabel from "../components/AccountLabel";
import { useLanguage } from "../context/LanguageContext";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

const CexStatistics = () => {
  const { t, language } = useLanguage();
  
  interface CexBalance {
    Address: string;
    Rank: number;
    Url: string;
    SignUp: string;
    Logo: string;
    Name: string;
    Balance: number;
    total_in?: number;
    total_out?: number;
    net_difference?: number;
  }

  interface TeamBalance {
    Address: string;
    Balance: number;
    total_in?: number;
    total_out?: number;
    net_difference?: number;
  }
  
  interface AccountBalanceFlow {
    account: string;
    net_difference: number;
    total_in: number;
    total_out: number;
  }

  interface BalanceFlowResponse {
    data: AccountBalanceFlow[];
    generated_at: string;
  }

  const [cexBalances, setCexBalances] = useState<CexBalance[]>([]);
  const [teamsBalances, setTeamsBalances] = useState<TeamBalance[]>([]);
  const [accountFlowData, setAccountFlowData] = useState<{[key: string]: AccountBalanceFlow}>({});
  const [loading, setLoading] = useState(false);
  const [piPriceUSD, setPiPriceUSD] = useState(0.0);
  const [flowGeneratedAt, setFlowGeneratedAt] = useState<string>("");
  const {setHeading, setTitle, setDescription} = usePageMetadata();

  useEffect(() => {
    setHeading(t('cex.heading'));
    setTitle(t('cex.title'));
    setDescription(t('cex.description'));
  }, [setTitle, setDescription, setHeading, t, language]);

  // Fetch Pi price
  const fetchPiPrice = async () => {
    try {
      okx.getMarketData().then(marketData => {
        setPiPriceUSD(parseFloat(marketData.idxPx));
      });
    } catch (error) {
      console.error("Error fetching Pi price:", error);
    }
  };

  // Fetch account balance flow data
  const fetchAccountBalanceFlow = async () => {
    try {
      const response = await fetch("https://api.piscan.io/data/account-balance-flow");
      const data: BalanceFlowResponse = await response.json();
      
      // Convert array to object with account as key for easier lookup
      const flowDataMap: {[key: string]: AccountBalanceFlow} = {};
      data.data.forEach(item => {
        flowDataMap[item.account] = item;
      });
      
      setAccountFlowData(flowDataMap);
      setFlowGeneratedAt(data.generated_at);
    } catch (error) {
      console.error("Error fetching account balance flow:", error);
    }
  };

  // Fetch CEX balances
  const fetchCexBalances = async () => {
    try {
      setLoading(true);
      const dataCexs = await horizon.getCexData();
      
      // Merge with flow data
      const enrichedCexData = dataCexs.map(cex => {
        const flowData = accountFlowData[cex.Address];
        return {
          ...cex,
          total_in: flowData?.total_in || 0,
          total_out: flowData?.total_out || 0,
          net_difference: flowData?.net_difference || 0
        };
      });
      
      setCexBalances(enrichedCexData);
    } catch (error) {
      console.error("Error fetching CEX balances:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch team balances
  const fetchTeamBalances = async () => {
    try {
      setLoading(true);
      const balances = await horizon.getBalances(coreTeamAddress);
      
      // Merge with flow data
      const enrichedTeamData = balances.map(item => {
        const flowData = accountFlowData[item.Address];
        return {
          ...item,
          total_in: flowData?.total_in || 0,
          total_out: flowData?.total_out || 0,
          net_difference: flowData?.net_difference || 0
        };
      });
      
      setTeamsBalances(enrichedTeamData);
    } catch (error) {
      console.error("Error fetching team balances:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetching
  useEffect(() => {
    fetchPiPrice();
    fetchAccountBalanceFlow();
  }, []);

  // Second phase data fetching (after price and flow data are available)
  useEffect(() => {
    if (piPriceUSD && Object.keys(accountFlowData).length > 0) {
      fetchCexBalances();
      fetchTeamBalances();
    }
  }, [piPriceUSD, accountFlowData]);

  // Format number according to user locale
  const formatNumber = (value: number): string => {
    return value.toLocaleString(language, {  
      minimumFractionDigits: 0,  
      maximumFractionDigits: 2 
    });
  };

  // Format currency according to user locale
  const formatCurrency = (value: number): string => {
    return value.toLocaleString(language, {  
      minimumFractionDigits: 0,  
      maximumFractionDigits: 2 
    });
  };

  // Format net difference with color and sign
  const formatNetDifference = (value: number): JSX.Element => {
    const isPositive = value >= 0;
    const color = isPositive ? "success" : "danger";
    const sign = isPositive ? "+" : "";
    
    return (
      <span className={`text-${color}`}>
        {sign}{formatNumber(value)}
      </span>
    );
  };

  return (
    <div className="">
      {loading ? (
        <div className="d-flex justify-content-center">
          <Spinner animation="border" variant="primary" />
          <p className="ms-2">{t('common.loading')}</p>
        </div>
      ) : (
        <div>
          {flowGeneratedAt && (
            <div className="mb-2 small text-muted">
              <i className="bi bi-info-circle me-1"></i>
              {t('cex.flow_data_updated')}: {new Date(flowGeneratedAt).toLocaleString()}
            </div>
          )}
          
          <div className="col-md-12">
            <Card className="none-radius">
              <CardContent>
                <h5 className="card-title" style={{fontSize:"16px",marginBottom:"20px"}}>
                  {t('cex.wallet_balances')}
                </h5>
                <Table hover responsive className="cex-table">
                  <thead>
                    <tr>
                      <th>{t('cex.cmc_ranking')}</th>
                      <th>{t('cex.exchange')}</th>
                      <th>{t('cex.chart_link')}</th>
                      <th>{t('cex.signup_link')}</th>
                      <th>{t('cex.wallet_address')}</th>
                      <th>{t('cex.balance_pi')}</th>
                      <th>{t('cex.total_in')}</th>
                      <th>{t('cex.total_out')}</th>
                      <th>{t('cex.net_difference')}</th>
                      <th>{t('cex.balance_usd')}</th>
                      <th>{t('cex.actions', { defaultValue: 'Actions' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cexBalances.map((cex, index) => (
                      <tr key={index}>
                        <td>{cex.Rank}</td>
                        <td>
                          <a href={cex.SignUp} target="_blank" rel="noreferrer" className="text-decoration-none">
                            <img src={cex.Logo} alt={cex.Name} className="cex-logo" width={20} />
                            {" "}
                            <span className="cex-name">{cex.Name}</span>
                          </a>
                        </td>
                        <td>
                          <a 
                            href={cex.Url} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                          >
                            <i className="bi bi-graph-up me-1"></i> {t('cex.chart')}
                          </a>
                        </td>
                        <td>
                          <a 
                            href={cex.SignUp} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                          >
                            <i className="bi bi-person-plus me-1"></i> {t('cex.signup')}
                          </a>
                        </td>
                        <td>
                          <Link className="text-decoration-none" to={`/account/${cex.Address}`}>
                            {cex.Address.slice(0,8)}...{cex.Address.slice(-5)}
                          </Link>{" "} <CopyIcon textToCopy={cex.Address}/>
                        </td>
                        <td>{formatNumber(cex.Balance)}</td>
                        <td>
                          {(cex.total_in ?? 0) > 0 ? (
                            <Badge bg="success" className="rounded-pill">{formatNumber(cex.total_in ?? 0)}</Badge>
                          ) : "0"}
                        </td>
                        <td>
                          {(cex.total_out ?? 0) > 0 ? (
                            <Badge bg="danger" className="rounded-pill">{formatNumber(cex.total_out ?? 0)}</Badge>
                          ) : "0"}
                        </td>
                        <td>{formatNetDifference(cex.net_difference || 0)}</td>
                        <td>${formatCurrency(cex.Balance * piPriceUSD)}</td>
                        <td>
                          <Link 
                            to={`/txs/filter?address=${cex.Address}`}
                            className="btn btn-sm btn-outline-info d-inline-flex align-items-center"
                            title={t('cex.filter_transactions_tooltip', { defaultValue: 'Filter transactions for this address' })}
                          >
                            <i className="bi bi-filter me-1"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{fontWeight:"600"}}>
                      <td colSpan={5}>{t('cex.total')}</td>
                      <td>{formatNumber(cexBalances.reduce((a, b) => a + b.Balance, 0))}</td>
                      <td>{formatNumber(cexBalances.reduce((a, b) => a + (b.total_in || 0), 0))}</td>
                      <td>{formatNumber(cexBalances.reduce((a, b) => a + (b.total_out || 0), 0))}</td>
                      <td>{formatNetDifference(cexBalances.reduce((a, b) => a + (b.net_difference || 0), 0))}</td>
                      <td>${formatCurrency(cexBalances.reduce((a, b) => a + b.Balance, 0) * piPriceUSD)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </CardContent>
            </Card>
          </div>
             
          {/* <div className="row g-3 mb-4 d-block d-md-none">
            <AdUnit
              className="67cca9819302154e82f055f8"
              width="300px"
              height="100px"
              centered={true}
            />
          </div> */}
      
          <div className="col-md-12" style={{marginTop: "20px"}}>
            <Card className="none-radius">
              <CardContent>
                <h5 className="card-title" style={{fontSize:"16px",marginBottom:"20px"}}>{t('cex.core_team_wallets')}</h5>
                <Table hover responsive className="cex-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('cex.note')}</th>
                      <th>{t('cex.wallet_address')}</th>
                      <th>{t('cex.balance_pi')}</th>
                      <th>{t('cex.total_in')}</th>
                      <th>{t('cex.total_out')}</th>
                      <th>{t('cex.net_difference')}</th>
                      <th>{t('cex.balance_usd')}</th>
                      <th>{t('cex.actions', { defaultValue: 'Actions' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamsBalances.map((item, index) => (
                      <tr key={index}>
                        <td>{index}</td>
                        <td>
                          <span className="cex-name">{LabledAddress[item.Address]!=null?LabledAddress[item.Address].Name:""}</span>
                        </td>
                        <td>
                          <Link className="text-decoration-none" to={`/account/${item.Address}`}>
                            {item.Address.slice(0,8)}...{item.Address.slice(-5)}
                          </Link>
                          {" "}<CopyIcon textToCopy={item.Address} />
                        </td>
                        <td>{formatNumber(item.Balance)}</td>
                        <td>
                          {(item.total_in ?? 0) > 0 ? (
                            <Badge bg="success" className="rounded-pill">{formatNumber(item.total_in ?? 0)}</Badge>
                          ) : "0"}
                        </td>
                        <td>
                          {(item.total_out ?? 0) > 0 ? (
                            <Badge bg="danger" className="rounded-pill">{formatNumber(item.total_out ?? 0)}</Badge>
                          ) : "0"}
                        </td>
                        <td>{formatNetDifference(item.net_difference || 0)}</td>
                        <td>${formatCurrency(item.Balance * piPriceUSD)}</td>
                        <td>
                          <Link 
                            to={`/txs/filter?address=${item.Address}`}
                            className="btn btn-sm btn-outline-info d-inline-flex align-items-center"
                            title={t('cex.filter_transactions_tooltip', { defaultValue: 'Filter transactions for this address' })}
                          >
                            <i className="bi bi-filter me-1"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{fontWeight:"600"}}>
                      <td colSpan={1}>{t('cex.total')}</td>
                      <td colSpan={2} style={{fontWeight:400}}><i>{t('cex.team_wallet_footnote')}</i></td>
                      <td>{formatNumber(teamsBalances.reduce((a, b) => a + b.Balance, 0))}</td>
                      <td>{formatNumber(teamsBalances.reduce((a, b) => a + (b.total_in || 0), 0))}</td>
                      <td>{formatNumber(teamsBalances.reduce((a, b) => a + (b.total_out || 0), 0))}</td>
                      <td>{formatNetDifference(teamsBalances.reduce((a, b) => a + (b.net_difference || 0), 0))}</td>
                      <td>${formatCurrency(teamsBalances.reduce((a, b) => a + b.Balance, 0) * piPriceUSD)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default CexStatistics;
